import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import MoveBookingDialog from '@/components/MoveBookingDialog';
import { isDepositUnpaid, formatDepositAmount, confirmUnpaidDepositArrival } from '@/lib/deposit';
import { useAuth } from '@/context/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Search, 
  Plus, 
  Calendar as CalendarIcon, 
  Phone, 
  Mail,
  Users,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
  Loader2,
  Filter,
  X,
  CreditCard,
  Send,
  RefreshCw,
  AlertTriangle,
  Copy,
  ExternalLink,
  MessageSquare,
  Delete,
  Check,
  CheckCircle,
  UserCheck,
  UserX,
  Table2,
  CalendarClock,
  Lock,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Sort key: local calendar date + time (handles VIP / early slots vs creation order). */
function bookingDateTimeMs(b) {
  const ymd = String(b.booking_date || '').slice(0, 10);
  const raw = String(b.booking_time || '00:00').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  let hh = 0;
  let mm = 0;
  let ss = 0;
  if (match) {
    hh = parseInt(match[1], 10);
    mm = parseInt(match[2], 10);
    ss = match[3] ? parseInt(match[3], 10) : 0;
  }
  const isoLocal = `${ymd}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  const t = Date.parse(isoLocal);
  return Number.isNaN(t) ? 0 : t;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

function assignedTablesLabelForList(booking, tablesList) {
  const ids = booking.assigned_tables;
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const names = ids.map((tid) => tablesList.find((t) => t.id === tid)?.name).filter(Boolean);
  if (names.length) return names.join(' · ');
  return ids.map((id) => `ID ${String(id).slice(0, 8)}…`).join(' · ');
}

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'seated', label: 'Seated' },
  { value: 'completed', label: 'Completed' },
  { value: 'no-show', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function AdminBookings() {
  const { restaurant, user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(() => new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(null);
  const [resendingConfirmation, setResendingConfirmation] = useState(null);
  const [markingDepositPaid, setMarkingDepositPaid] = useState(null);
  const [verifyingDeposit, setVerifyingDeposit] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const isAdminUser = user?.role === 'admin' || user?.role === 'platform_admin';
  const [phoneBookingStep, setPhoneBookingStep] = useState(1);
  const [phoneData, setPhoneData] = useState({
    customer_phone: '',
    customer_name: '',
    customer_email: '',
    party_size: '2',
    allergies: '',
    special_requests: '',
    booking_date: null,
    booking_time: '',
  });
  const [customerFound, setCustomerFound] = useState(false);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [phoneDepositInfo, setPhoneDepositInfo] = useState(null);
  const [phoneWaiveDeposit, setPhoneWaiveDeposit] = useState(false);
  const [sendingDepositEmail, setSendingDepositEmail] = useState(false);
  const phoneSubmitLockRef = useRef(false);
  const [depositLinkDialog, setDepositLinkDialog] = useState({
    open: false,
    url: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
  });
  const [tables, setTables] = useState([]);
  const [tableAvailability, setTableAvailability] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter, dateFilter]);

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter === 'deposit_unpaid') params.append('deposit', 'unpaid');
      else if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFilter) params.append('date', format(dateFilter, 'yyyy-MM-dd'));
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/admin/bookings?${params}`);
      setBookings(response.data.bookings);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings();
  };

  const openCreateDialog = () => {
    setEditingBooking(null);
    resetPhoneBooking();
    setDialogOpen(true);
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/admin/tables`);
      setTables(response.data.tables || []);
    } catch (error) {
      console.error('Failed to load tables:', error);
    }
  };

  const resetPhoneBooking = () => {
    setPhoneBookingStep(1);
    setPhoneData({
      customer_phone: '',
      customer_name: '',
      customer_email: '',
      party_size: '2',
      allergies: '',
      special_requests: '',
      booking_date: null,
      booking_time: '',
    });
    setCustomerFound(false);
    setAvailableSlots([]);
    setPhoneDepositInfo(null);
    setPhoneWaiveDeposit(!!user?.phone_bookings_skip_deposit);
  };

  const openEditDialog = (booking) => {
    setEditingBooking({
      ...booking,
      party_size: parseInt(String(booking.party_size || 2), 10),
      assigned_tables: booking.assigned_tables || [],
    });
    recomputeTableAvailability(booking);
    setDialogOpen(true);
  };

  const recomputeTableAvailability = async (booking) => {
    if (!booking?.booking_date || !booking?.booking_time) return;
    try {
      const checks = await Promise.all(
        (tables || [])
          .filter((t) => t.is_active !== false)
          .map(async (t) => {
            const response = await axios.get(`${API}/admin/tables/availability`, {
              params: {
                date: booking.booking_date,
                time: booking.booking_time,
                exclude_booking_id: booking.id,
              },
            });
            const available = (response.data.tables || []).find((x) => x.id === t.id)?.is_available;
            return [t.id, Boolean(available)];
          })
      );
      setTableAvailability(Object.fromEntries(checks));
    } catch (error) {
      console.error('Failed to check table availability:', error);
    }
  };

  const toggleTableAssignment = (tableId, options = {}) => {
    if (!editingBooking) return;
    const current = editingBooking.assigned_tables || [];
    const isAssigned = current.includes(tableId);
    const isAvailable = tableAvailability[tableId] === true;
    if (!isAssigned && !isAvailable) {
      toast.error('That table is unavailable for this booking time');
      return;
    }
    const addTable = !!options.addTable;
    const next = isAssigned
      ? current.filter((id) => id !== tableId)
      : (addTable ? [...current, tableId] : [tableId]);
    setEditingBooking((prev) => ({ ...prev, assigned_tables: next }));
  };

  const appendPhoneLookupDigit = (digit) => {
    setPhoneData((prev) => ({
      ...prev,
      customer_phone: `${prev.customer_phone || ''}${digit}`.slice(0, 20),
    }));
  };

  const backspacePhoneLookupDigit = () => {
    setPhoneData((prev) => ({
      ...prev,
      customer_phone: (prev.customer_phone || '').slice(0, -1),
    }));
  };

  const clearPhoneLookupDigits = () => {
    setPhoneData((prev) => ({ ...prev, customer_phone: '' }));
  };

  const handlePhoneLookup = async () => {
    if (!phoneData.customer_phone || phoneData.customer_phone.length < 6) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSearchingCustomer(true);
    try {
      const response = await axios.get(`${API}/tablet/customer-lookup`, {
        params: { phone: phoneData.customer_phone },
      });
      if (response.data.found) {
        const customer = response.data.customer || {};
        setPhoneData((prev) => ({
          ...prev,
          customer_name: customer.name || '',
          customer_email: customer.email || '',
          allergies: customer.allergies || '',
        }));
        setCustomerFound(true);
        toast.success('Customer found! Details auto-filled.');
      } else {
        setCustomerFound(false);
        toast.info('New customer - please fill in details');
      }
      setPhoneBookingStep(2);
    } catch (error) {
      toast.error('Error looking up customer');
      setPhoneBookingStep(2);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const handlePhoneDateSelect = async (date) => {
    setPhoneData((prev) => ({ ...prev, booking_date: date, booking_time: '' }));
    if (!date) {
      setAvailableSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/availability/${restaurant.id}`, {
        params: {
          date: format(date, 'yyyy-MM-dd'),
          party_size: parseInt(phoneData.party_size, 10),
        },
      });
      const available = (response.data.slots || []).filter((slot) => slot.available_tables > 0);
      setAvailableSlots(available);
      if (!available.length) toast.error('No availability for this date');
    } catch (error) {
      toast.error('Failed to load availability');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const checkPhoneDepositRequired = async () => {
    if (!phoneData.booking_date) return false;
    try {
      const response = await axios.post(`${API}/deposit/check`, {
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        party_size: parseInt(phoneData.party_size, 10),
      });
      setPhoneDepositInfo(response.data);
      return response.data.deposit_required;
    } catch (error) {
      setPhoneDepositInfo(null);
      return false;
    }
  };

  const createPhoneBooking = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/tablet/bookings/phone`, {
        customer_name: phoneData.customer_name,
        customer_email: phoneData.customer_email?.trim() || null,
        customer_phone: phoneData.customer_phone,
        party_size: parseInt(phoneData.party_size, 10),
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        booking_time: phoneData.booking_time,
        allergies: phoneData.allergies || null,
        special_requests: phoneData.special_requests || null,
        waive_deposit: phoneWaiveDeposit,
      });
      setDialogOpen(false);
      resetPhoneBooking();
      toast.success('Phone booking created');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const sendDepositEmail = async () => {
    if (!phoneData.customer_email || !phoneData.customer_email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSendingDepositEmail(true);
    try {
      const response = await axios.post(`${API}/tablet/bookings/send-deposit-link`, {
        customer_name: phoneData.customer_name,
        customer_email: phoneData.customer_email,
        customer_phone: phoneData.customer_phone,
        party_size: parseInt(phoneData.party_size, 10),
        restaurant_id: restaurant.id,
        booking_date: format(phoneData.booking_date, 'yyyy-MM-dd'),
        booking_time: phoneData.booking_time,
        allergies: phoneData.allergies || null,
        special_requests: phoneData.special_requests || null,
      });
      setDialogOpen(false);
      resetPhoneBooking();
      fetchBookings();
      if (response.data.checkout_url) {
        setDepositLinkDialog({
          open: true,
          url: response.data.checkout_url,
          customerName: phoneData.customer_name,
          customerPhone: phoneData.customer_phone,
          customerEmail: phoneData.customer_email,
        });
      }
      toast.success('Deposit payment link sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send deposit link');
    } finally {
      setSendingDepositEmail(false);
    }
  };

  const handlePhoneBookingSubmit = async () => {
    if (!phoneData.customer_name || !phoneData.booking_date || !phoneData.booking_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    const emailOk = phoneData.customer_email && phoneData.customer_email.includes('@');
    if (!phoneWaiveDeposit && !emailOk) {
      toast.error('Email is required unless you choose no deposit required for this booking.');
      return;
    }

    if (phoneSubmitLockRef.current || submitting || sendingDepositEmail) {
      return;
    }
    phoneSubmitLockRef.current = true;

    try {
      if (phoneWaiveDeposit) {
        await createPhoneBooking();
        return;
      }

      const depositRequired = await checkPhoneDepositRequired();
      if (depositRequired) {
        await sendDepositEmail();
        return;
      }
      await createPhoneBooking();
    } finally {
      phoneSubmitLockRef.current = false;
    }
  };

  const handleEditSave = async () => {
    if (!editingBooking) return;
    const original = bookings.find((b) => b.id === editingBooking.id);
    if (original?.status !== editingBooking.status && !confirmUnpaidDepositArrival(editingBooking, editingBooking.status)) {
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/tablet/bookings/${editingBooking.id}`, {
        status: editingBooking.status,
        party_size: parseInt(String(editingBooking.party_size || 2), 10),
        allergies: editingBooking.allergies || null,
        special_requests: editingBooking.special_requests || null,
        assigned_tables: editingBooking.assigned_tables || [],
      });
      toast.success('Booking updated');
      setDialogOpen(false);
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancel = async (booking) => {
    if (!booking) return;

    if (booking.deposit_required && booking.deposit_status === 'paid' && booking.deposit_locked && !isAdminUser) {
      if (!window.confirm('This deposit is locked (booking was moved inside the non-refundable period). Cancel the booking and keep the deposit?')) {
        return;
      }
    } else if (booking.deposit_required && booking.deposit_status === 'paid') {
      const choice = window.prompt(
        booking.deposit_locked
          ? 'This deposit is LOCKED (booking was moved inside the non-refundable period). Type one option:\n\nnone = cancel and keep the deposit\nfull = cancel and refund the full deposit (admin override)\nfees = cancel and refund deposit minus Stripe card fees (admin override)'
          : 'This booking has a paid deposit. Type one option:\n\nfull = cancel and refund the full deposit\nfees = cancel and refund deposit minus Stripe card fees\nnone = cancel without refund',
        booking.deposit_locked ? 'none' : 'full'
      );

      if (choice === null) return;
      const normalized = choice.trim().toLowerCase();
      if (!['full', 'fees', 'none'].includes(normalized)) {
        toast.error('Cancellation not completed. Please type full, fees, or none.');
        return;
      }

      if (normalized === 'full' || normalized === 'fees') {
        await handleRefund(booking, normalized === 'fees' ? 'minus_fees' : 'full');
        return;
      }
    } else if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bookings/${booking.id}`);
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleDeleteCancelled = async (booking) => {
    if (!booking) return;
    if (!window.confirm('Permanently delete this cancelled booking? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${booking.id}/permanent`);
      toast.success('Cancelled booking deleted');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cancelled booking');
    }
  };

  const handleSendPaymentLink = async (bookingId) => {
    setSendingPaymentLink(bookingId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/admin/bookings/${bookingId}/send-payment-link?origin_url=${encodeURIComponent(originUrl)}`
      );
      toast.success(`Payment link sent to ${response.data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send payment link');
    } finally {
      setSendingPaymentLink(null);
    }
  };

  const handleMarkDepositPaid = async (booking) => {
    if (!booking?.id) return;
    const defaultAmount =
      typeof booking.deposit_amount === 'number' && Number.isFinite(booking.deposit_amount)
        ? booking.deposit_amount.toFixed(2)
        : '0.00';
    const raw = window.prompt(
      'Enter amount paid over phone (e.g. 25.00):',
      defaultAmount
    );
    if (raw === null) return;
    const amountPaid = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(amountPaid) || amountPaid <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    if (!window.confirm(`Mark deposit as paid (${(CURRENCY_SYMBOLS[booking.deposit_currency] || '£')}${amountPaid.toFixed(2)})?`)) {
      return;
    }
    setMarkingDepositPaid(booking.id);
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/mark-deposit-paid`, {
        amount_paid: amountPaid,
      });
      toast.success('Deposit marked as paid');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark deposit as paid');
    } finally {
      setMarkingDepositPaid(null);
    }
  };

  const handleVerifyDeposit = async (booking) => {
    if (!booking?.id) return;
    setVerifyingDeposit(booking.id);
    try {
      const response = await axios.post(`${API}/admin/bookings/${booking.id}/verify-deposit`);
      if (response.data.verified) {
        toast.success(response.data.message || 'Stripe confirms this deposit was paid');
        fetchBookings();
      } else {
        toast.error(response.data.message || 'Stripe has not confirmed this payment yet');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify deposit with Stripe');
    } finally {
      setVerifyingDeposit(null);
    }
  };

  const handleRefund = async (booking, refundPolicy = 'venue') => {
    const bookingId = typeof booking === 'string' ? booking : booking?.id;
    if (!bookingId) return;

    const lockedBooking = typeof booking === 'object' && booking?.deposit_locked;
    const lockedPrefix = lockedBooking
      ? 'WARNING: this deposit was locked because the booking was moved inside the non-refundable period. Refunding overrides that.\n\n'
      : '';
    const message = lockedPrefix + (refundPolicy === 'minus_fees'
      ? 'Refund this deposit minus Stripe card fees and cancel the booking? This cannot be undone.'
      : refundPolicy === 'full'
        ? 'Refund the full deposit and cancel the booking? This cannot be undone.'
        : 'Refund this deposit using the venue refund-fee setting and cancel the booking? This cannot be undone.');
    if (!window.confirm(message)) return;

    setRefunding(bookingId);
    try {
      const response = await axios.post(
        `${API}/admin/bookings/${bookingId}/refund?refund_policy=${encodeURIComponent(refundPolicy)}`
      );
      const symbol = CURRENCY_SYMBOLS[response.data.currency] || '£';
      const feeText = response.data.transaction_fee_deducted > 0
        ? ` (${symbol}${response.data.transaction_fee_deducted.toFixed(2)} Stripe fee deducted)`
        : '';
      toast.success(`Refund of ${symbol}${response.data.amount?.toFixed(2)} processed${feeText}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefunding(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-blue-100 text-blue-800',
      pending_deposit: 'bg-amber-100 text-amber-800',
      arrived: 'bg-amber-100 text-amber-800',
      seated: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-slate-100 text-slate-800',
      cancelled: 'bg-red-100 text-red-800',
      'no-show': 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.confirmed;
  };

  const getDepositBadge = (booking) => {
    if (!booking.deposit_required) return null;
    const symbol = CURRENCY_SYMBOLS[booking.deposit_currency] || '£';
    if (booking.deposit_status === 'paid') {
      const manualLabel = booking.deposit_payment_method === 'manual_phone' ? ' (Phone)' : '';
      return (
        <span className="inline-flex flex-wrap gap-1">
          <Badge className="bg-emerald-100 text-emerald-800">Deposit Paid{manualLabel} {symbol}{booking.deposit_amount?.toFixed(2)}</Badge>
          {booking.deposit_locked && (
            <Badge className="bg-amber-100 text-amber-900" title="Moved inside the non-refundable period: deposit kept">
              <Lock className="h-3 w-3 mr-1" /> Locked, non-refundable
            </Badge>
          )}
        </span>
      );
    } else if (booking.deposit_status === 'refunded') {
      return <Badge className="bg-purple-100 text-purple-800">Refunded</Badge>;
    } else if (isDepositUnpaid(booking)) {
      return (
        <Badge className="bg-red-600 text-white hover:bg-red-600" data-testid="deposit-not-paid">
          <AlertTriangle className="h-3 w-3 mr-1" /> DEPOSIT NOT PAID {formatDepositAmount(booking)}
        </Badge>
      );
    }
    return null;
  };

  const getLatestCustomerEmailEvent = (booking) => {
    const events = Array.isArray(booking?.email_delivery_events) ? booking.email_delivery_events : [];
    const customerEmail = String(booking?.customer_email || '').trim().toLowerCase();
    const matching = events.filter((e) => {
      const to = String(e?.to_email || '').trim().toLowerCase();
      return to && to === customerEmail;
    });
    if (matching.length === 0) return null;
    return matching[matching.length - 1];
  };

  const handleResendConfirmation = async (booking) => {
    const current = booking?.customer_email || '';
    const nextEmailRaw = window.prompt('Edit email if needed, then resend confirmation:', current);
    if (nextEmailRaw === null) return;
    const nextEmail = nextEmailRaw.trim();
    if (!nextEmail || !nextEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setResendingConfirmation(booking.id);
    try {
      const response = await axios.post(`${API}/admin/bookings/${booking.id}/resend-confirmation`, {
        customer_email: nextEmail,
      });
      toast.success(response.data?.message || 'Confirmation email resent');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend confirmation email');
    } finally {
      setResendingConfirmation(null);
    }
  };

  const filteredBookings = useMemo(() => {
    const filtered = bookings.filter((b) => {
      if (search) {
        const searchLower = search.toLowerCase();
        const phone = b.customer_phone || '';
        return (
          b.customer_name.toLowerCase().includes(searchLower) ||
          b.customer_email.toLowerCase().includes(searchLower) ||
          phone.includes(search)
        );
      }
      return true;
    });
    return [...filtered].sort((a, b) => bookingDateTimeMs(a) - bookingDateTimeMs(b));
  }, [bookings, search]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-bookings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Bookings</h1>
            <p className="text-muted-foreground mt-1">Manage all your restaurant reservations</p>
          </div>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open && !editingBooking) resetPhoneBooking();
            }}
          >
            <DialogTrigger asChild>
              <Button className="gap-2" onClick={openCreateDialog} data-testid="add-booking-btn">
                <Plus className="h-4 w-4" />
                Add Booking
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingBooking ? 'Edit Booking' : 'Phone Booking'}
                </DialogTitle>
                <DialogDescription>
                  {editingBooking
                    ? 'Update booking details'
                    : phoneBookingStep === 1
                      ? 'Enter customer phone to find their details'
                      : phoneBookingStep === 2
                        ? 'Confirm customer details and party size'
                        : 'Select date and available time'}
                </DialogDescription>
              </DialogHeader>
              {editingBooking ? (
                <div className="space-y-4">
                  {((editingBooking.source === 'walk-in' && !editingBooking.staff_only) ||
                    editingBooking.staff_only) &&
                    editingBooking.created_by_user_name && (
                      <p className="truncate rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm font-semibold text-red-950">
                        Added by {editingBooking.created_by_user_name}
                      </p>
                    )}
                  <div className="p-3 bg-secondary/40 rounded-lg">
                    <p className="font-medium">{editingBooking.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{editingBooking.booking_time} • {editingBooking.party_size} guests</p>
                  </div>

                  {editingBooking.allergies && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-amber-800">Allergies / Dietary</p>
                      <p className="text-sm text-amber-700">{editingBooking.allergies}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {STATUS_OPTIONS.map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          variant={editingBooking.status === opt.value ? 'default' : 'outline'}
                          className="justify-start"
                          onClick={() => setEditingBooking((prev) => ({ ...prev, status: opt.value }))}
                        >
                          {opt.value === 'confirmed' && <Check className="h-4 w-4 mr-2" />}
                          {opt.value === 'arrived' && <UserCheck className="h-4 w-4 mr-2" />}
                          {opt.value === 'seated' && <Users className="h-4 w-4 mr-2" />}
                          {opt.value === 'completed' && <CheckCircle className="h-4 w-4 mr-2" />}
                          {opt.value === 'no-show' && <UserX className="h-4 w-4 mr-2" />}
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Party Size</Label>
                    <Select
                      value={String(editingBooking.party_size || 2)}
                      onValueChange={(v) => setEditingBooking((prev) => ({ ...prev, party_size: parseInt(v, 10) }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n} guests</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Allergies / Dietary
                    </Label>
                    <Textarea
                      value={editingBooking.allergies || ''}
                      onChange={(e) => setEditingBooking((prev) => ({ ...prev, allergies: e.target.value }))}
                      placeholder="e.g., Nut allergy, Gluten-free"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Special Requests</Label>
                    <Textarea
                      value={editingBooking.special_requests || ''}
                      onChange={(e) => setEditingBooking((prev) => ({ ...prev, special_requests: e.target.value }))}
                      placeholder="e.g., Birthday table, quiet area, highchair"
                      rows={3}
                    />
                  </div>

                  {tables.length > 0 && (
                    <div className="space-y-2">
                      <Label>Assigned tables</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {tables.filter((t) => t.is_active !== false).map((table) => {
                          const isAssigned = editingBooking.assigned_tables?.includes(table.id);
                          const isAvailable = tableAvailability[table.id] === true;
                          const isUnavailable = !isAssigned && !isAvailable;
                          return (
                            <Button
                              key={table.id}
                              type="button"
                              variant={isAssigned ? 'default' : isUnavailable ? 'secondary' : 'outline'}
                              size="sm"
                              className="h-auto min-h-12 flex-col py-1"
                              disabled={isUnavailable}
                              onClick={(e) => toggleTableAssignment(table.id, { addTable: e.shiftKey })}
                            >
                              <span className="font-semibold">{table.name}</span>
                              <span className="text-[10px] opacity-70">{table.capacity} seats</span>
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                    <Button type="button" onClick={handleEditSave} disabled={savingEdit} className="flex-1">
                      {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {phoneBookingStep === 1 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Phone Number</Label>
                        <Input
                          value={phoneData.customer_phone}
                          onChange={(e) => setPhoneData({ ...phoneData, customer_phone: e.target.value })}
                          placeholder="+1 555-0000"
                          autoFocus
                          data-testid="phone-lookup-input"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                          <Button
                            key={digit}
                            type="button"
                            variant="outline"
                            className="h-12 text-lg font-semibold touch-manipulation"
                            onClick={() => appendPhoneLookupDigit(digit)}
                          >
                            {digit}
                          </Button>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 text-sm touch-manipulation"
                          onClick={clearPhoneLookupDigits}
                        >
                          Clear
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 text-lg font-semibold touch-manipulation"
                          onClick={() => appendPhoneLookupDigit('0')}
                        >
                          0
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-12 touch-manipulation"
                          onClick={backspacePhoneLookupDigit}
                        >
                          <Delete className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                        <Button onClick={handlePhoneLookup} disabled={searchingCustomer} className="flex-1" data-testid="phone-lookup-btn">
                          {searchingCustomer ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Find Customer'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {phoneBookingStep === 2 && (
                    <div className="space-y-4">
                      {customerFound && (
                        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
                          ✓ Returning customer - details auto-filled
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input value={phoneData.customer_name} onChange={(e) => setPhoneData({ ...phoneData, customer_name: e.target.value })} placeholder="Customer name" />
                      </div>
                      <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                        <Checkbox
                          id="phone-waive-deposit"
                          checked={phoneWaiveDeposit}
                          onCheckedChange={(v) => setPhoneWaiveDeposit(v === true)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="phone-waive-deposit" className="cursor-pointer font-medium leading-none">
                            No deposit required
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Confirm without a deposit or guest email. No deposit request email is sent.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email {phoneWaiveDeposit ? '(optional)' : '*'}</Label>
                        <Input type="email" value={phoneData.customer_email} onChange={(e) => setPhoneData({ ...phoneData, customer_email: e.target.value })} placeholder="email@example.com" />
                      </div>
                      <div className="space-y-2">
                        <Label>Party Size</Label>
                        <Select value={phoneData.party_size} onValueChange={(v) => setPhoneData({ ...phoneData, party_size: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => <SelectItem key={n} value={String(n)}>{n} guests</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Allergies / Dietary</Label>
                        <Input value={phoneData.allergies} onChange={(e) => setPhoneData({ ...phoneData, allergies: e.target.value })} placeholder="e.g., Nut allergy, Gluten-free" />
                      </div>
                      <div className="space-y-2">
                        <Label>Special Requests</Label>
                        <Textarea
                          value={phoneData.special_requests}
                          onChange={(e) => setPhoneData({ ...phoneData, special_requests: e.target.value })}
                          placeholder="e.g., Birthday table, quiet area, highchair"
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setPhoneBookingStep(1)} className="flex-1">Back</Button>
                        <Button
                          onClick={() => setPhoneBookingStep(3)}
                          disabled={
                            !phoneData.customer_name ||
                            (!phoneWaiveDeposit && (!phoneData.customer_email || !phoneData.customer_email.includes('@')))
                          }
                          className="flex-1"
                        >
                          Select Time
                        </Button>
                      </div>
                    </div>
                  )}

                  {phoneBookingStep === 3 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Select Date</Label>
                        <Calendar
                          mode="single"
                          selected={phoneData.booking_date}
                          onSelect={handlePhoneDateSelect}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          className="rounded-md border"
                        />
                      </div>
                      {phoneData.booking_date && (
                        <div className="space-y-2">
                          <Label>Available Times for {format(phoneData.booking_date, 'MMM d')}</Label>
                          {loadingSlots ? (
                            <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                          ) : availableSlots.length > 0 ? (
                            <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto">
                              {availableSlots.map((slot) => (
                                <Button
                                  key={slot.time}
                                  variant={phoneData.booking_time === slot.time ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setPhoneData({ ...phoneData, booking_time: slot.time })}
                                  className="h-auto min-h-12 flex-col gap-0.5 py-1.5"
                                  data-testid={`time-slot-${slot.time}`}
                                >
                                  <span>{slot.time}</span>
                                  <span className="text-[10px] opacity-80">{slot.available_tables}/{slot.total_tables || '-'} free</span>
                                </Button>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No available times for this date</p>
                          )}
                        </div>
                      )}
                      <div className="flex gap-3 pt-2">
                        <Button variant="outline" onClick={() => setPhoneBookingStep(2)} className="flex-1">Back</Button>
                        <Button onClick={handlePhoneBookingSubmit} disabled={!phoneData.booking_time || submitting} className="flex-1">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Booking'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-bookings"
                  />
                </div>
                <Button type="submit" variant="secondary">Search</Button>
              </form>
              
              <div className="flex gap-2">
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v);
                    // Unpaid deposits are useful across all dates, not just the selected day.
                    if (v === 'deposit_unpaid') setDateFilter(null);
                  }}
                >
                  <SelectTrigger className="w-[170px]" data-testid="status-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="arrived">Arrived</SelectItem>
                    <SelectItem value="seated">Seated</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="deposit_unpaid">Deposit not paid</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`gap-2 ${dateFilter ? 'rounded-r-none border-r-0' : ''}`}
                        data-testid="date-filter"
                      >
                        <CalendarIcon className="h-4 w-4" />
                        {dateFilter ? format(dateFilter, 'MMM d') : 'Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={dateFilter}
                        onSelect={setDateFilter}
                      />
                    </PopoverContent>
                  </Popover>
                  {dateFilter && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-8 rounded-l-none"
                      onClick={() => setDateFilter(null)}
                      aria-label="Clear date filter"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card className="border-border/50">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No bookings found</p>
                <p className="text-sm mt-1">Try adjusting your filters or add a new booking</p>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {filteredBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-4 sm:p-6 hover:bg-secondary/30 transition-colors cursor-pointer"
                    onClick={() => openEditDialog(booking)}
                    data-testid={`booking-row-${booking.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {((booking.source === 'walk-in' && !booking.staff_only) || booking.staff_only) &&
                          booking.created_by_user_name && (
                            <p
                              className="mb-2 truncate rounded-md border border-red-100 bg-red-50 px-2 py-1 text-xs font-semibold text-red-950"
                              title={`Added by ${booking.created_by_user_name}`}
                            >
                              Added by {booking.created_by_user_name}
                            </p>
                          )}
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold truncate">{booking.customer_name}</h3>
                          <Badge className={cn("shrink-0", getStatusBadge(booking.status))}>
                            {booking.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3.5 w-3.5" />
                            {format(new Date(booking.booking_date), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {booking.booking_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {booking.party_size} guests
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" />
                            {booking.customer_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {booking.customer_phone}
                          </span>
                        </div>
                        {(() => {
                          const latestEmail = getLatestCustomerEmailEvent(booking);
                          if (!latestEmail) return null;
                          const failed = latestEmail.status === 'failed' || latestEmail.status === 'bounced';
                          return (
                            <div className="mt-2">
                              <Badge className={failed ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}>
                                Email {failed ? 'failed' : 'sent'} ({latestEmail.email_type?.replace('_', ' ') || 'confirmation'})
                              </Badge>
                              {failed && latestEmail.error ? (
                                <p className="mt-1 text-xs text-red-700 break-words">{latestEmail.error}</p>
                              ) : null}
                            </div>
                          );
                        })()}
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                          <span
                            className="inline-flex min-w-0 items-center gap-1.5 font-medium text-foreground"
                            title={
                              Array.isArray(booking.assigned_tables) ? booking.assigned_tables.join(', ') : undefined
                            }
                          >
                            <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">
                              {assignedTablesLabelForList(booking, tables) || (
                                <span className="font-normal text-muted-foreground">No table assigned</span>
                              )}
                            </span>
                          </span>
                        </div>
                        {(() => {
                          const allergyText =
                            booking.allergies && String(booking.allergies).trim()
                              ? String(booking.allergies).trim()
                              : '';
                          return (
                            <div
                              className={cn(
                                'mt-2 flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
                                allergyText
                                  ? 'border-amber-400 bg-amber-100 font-semibold text-amber-950'
                                  : 'border-border bg-muted/50 text-muted-foreground'
                              )}
                              data-testid={`booking-allergies-${booking.id}`}
                            >
                              <AlertTriangle
                                className={cn('mt-0.5 h-4 w-4 shrink-0', !allergyText && 'opacity-45')}
                              />
                              <span className="min-w-0 break-words">
                                {allergyText ? (
                                  <>
                                    <span className="mr-1.5">Allergies:</span>
                                    {allergyText}
                                  </>
                                ) : (
                                  'Allergies: none noted'
                                )}
                              </span>
                            </div>
                          );
                        })()}
                        {/* Deposit Badge */}
                        {booking.deposit_required && (
                          <div className="mt-2">
                            {getDepositBadge(booking)}
                          </div>
                        )}
                        {booking.special_requests && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            "{booking.special_requests}"
                          </p>
                        )}
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`booking-menu-${booking.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => openEditDialog(booking)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!['cancelled', 'no-show', 'completed'].includes(booking.status) && booking.deposit_status !== 'refunded' && (
                            <DropdownMenuItem onClick={() => setMoveTarget(booking)} data-testid={`move-booking-${booking.id}`}>
                              <CalendarClock className="h-4 w-4 mr-2" />
                              Move Booking (keep deposit)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleResendConfirmation(booking)}
                            disabled={resendingConfirmation === booking.id}
                          >
                            {resendingConfirmation === booking.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4 mr-2" />
                            )}
                            Edit Email + Resend Confirmation
                          </DropdownMenuItem>
                          
                          {/* Send Payment Link - for pending deposits */}
                          {isDepositUnpaid(booking) && (
                            <DropdownMenuItem 
                              onClick={() => handleSendPaymentLink(booking.id)}
                              disabled={sendingPaymentLink === booking.id}
                            >
                              {sendingPaymentLink === booking.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4 mr-2" />
                              )}
                              Send Payment Link
                            </DropdownMenuItem>
                          )}
                          {isDepositUnpaid(booking) && booking.deposit_session_id && (
                            <DropdownMenuItem
                              onClick={() => handleVerifyDeposit(booking)}
                              disabled={verifyingDeposit === booking.id}
                            >
                              {verifyingDeposit === booking.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4 mr-2" />
                              )}
                              Verify with Stripe
                            </DropdownMenuItem>
                          )}
                          {booking.deposit_status !== 'refunded' && (
                            <DropdownMenuItem
                              onClick={() => handleMarkDepositPaid(booking)}
                              disabled={markingDepositPaid === booking.id}
                              className="text-emerald-700"
                            >
                              {markingDepositPaid === booking.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle className="h-4 w-4 mr-2" />
                              )}
                              Mark Deposit Paid (Phone)
                            </DropdownMenuItem>
                          )}
                          
                          {/* Refund - for paid deposits (locked deposits: admin only) */}
                          {booking.deposit_required && booking.deposit_status === 'paid' && (!booking.deposit_locked || isAdminUser) && (
                            <DropdownMenuItem 
                              onClick={() => handleRefund(booking)}
                              disabled={refunding === booking.id}
                              className="text-purple-600"
                            >
                              {refunding === booking.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4 mr-2" />
                              )}
                              {booking.deposit_locked ? 'Refund Locked Deposit (admin)' : 'Refund Deposit'}
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {booking.status !== 'cancelled' && (
                            <DropdownMenuItem 
                              onClick={() => handleCancel(booking)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Cancel Booking
                            </DropdownMenuItem>
                          )}
                          {booking.status === 'cancelled' && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteCancelled(booking)}
                              className="text-destructive"
                            >
                              <Delete className="h-4 w-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={depositLinkDialog.open}
          onOpenChange={(open) => setDepositLinkDialog((prev) => ({ ...prev, open }))}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-amber-600" />
                Send Deposit Link to Customer
              </DialogTitle>
              <DialogDescription>
                Booking created! Share this payment link with <strong>{depositLinkDialog.customerName}</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <Label className="text-xs text-muted-foreground mb-1 block">Payment Link</Label>
                <div className="flex gap-2">
                  <Input value={depositLinkDialog.url} readOnly className="font-mono text-xs bg-white" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(depositLinkDialog.url);
                      toast.success('Link copied');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {depositLinkDialog.customerPhone && (
                  <a
                    href={`https://wa.me/${depositLinkDialog.customerPhone.replace(/\D/g, '')}?text=${encodeURIComponent(
                      `Hi ${depositLinkDialog.customerName}! Please complete your booking deposit using this link: ${depositLinkDialog.url}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors"
                  >
                    <MessageSquare className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
                {depositLinkDialog.customerPhone && (
                  <a
                    href={`sms:${depositLinkDialog.customerPhone}?body=${encodeURIComponent(
                      `Hi ${depositLinkDialog.customerName}! Complete your booking deposit here: ${depositLinkDialog.url}`
                    )}`}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    SMS
                  </a>
                )}
                <a
                  href={`mailto:${depositLinkDialog.customerEmail}?subject=${encodeURIComponent('Complete Your Booking - Deposit Required')}&body=${encodeURIComponent(
                    `Hi ${depositLinkDialog.customerName},\n\nTo secure your reservation, please complete the deposit payment here:\n\n${depositLinkDialog.url}\n\nThank you.`
                  )}`}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm font-medium transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </a>
                <a
                  href={depositLinkDialog.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Link
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <MoveBookingDialog
        open={Boolean(moveTarget)}
        onOpenChange={(open) => { if (!open) setMoveTarget(null); }}
        booking={moveTarget}
        restaurantId={restaurant?.id}
        onMoved={() => fetchBookings()}
      />
    </AdminLayout>
  );
}
